import React from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { Footer as GrommetFooter, Paragraph, Box } from 'grommet';
import { GatsbyImage } from 'gatsby-plugin-image';
import Link from './Link';
import { SocialIcon, SocialItemsData } from './SocialIcons';

// SOCIAL ICONS ============

const SocialIcons = styled(Box)`
  display: inline-flex;
`;

const SocialIconsGroup = styled.div`
  grid-area: socialIcons;
  align-self: center;
  justify-self: center;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    justify-self: end;
  }
`;

// SITE LINKS ==============

const SiteLinks = styled.div`
  grid-area: siteLinks;
  a {
    font-weight: 700;
  }
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    align-self: center;
    ul {
      justify-content: flex-start;
    }
  }
`;

// LEGAL LINKS ==============

const LegalLinks = styled.div`
  grid-area: legalLinks;
  a {
    font-weight: 400;
  }
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    align-self: center;
    ul {
      justify-content: flex-end;
    }
  }
`;

// GENERAL FOOTER LINKS ====

const FooterLink = styled.li`
  list-style-type: none;
  text-align: center;

  @media ${({ theme }) => theme.custom.layouts.below_large} {
    & + & {
      margin-top: 20px;
    }
  }

  @media ${({ theme }) => theme.custom.layouts.above_large} {
    & + & {
      margin-left: 30px;
    }
  }
`;

const FooterLinkList = styled.ul`
  display: flex;
  flex-direction: column;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    width: 100%;
    flex-direction: row;
  }
`;

// SITE LOGO ===============

const StyledSiteLogo = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  grid-area: siteLogo;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    justify-content: flex-start;
  }

  img {
    max-width: 250px;
    height: 50px;
  }
`;

// PWC Link ================

const PWCLink = styled.div`
  grid-area: pwcLink;
  text-align: center;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    text-align: left;
  }
`;

// LEGAL DISCLAIMER ==============

const LegalDisclaimer = styled.div`
  grid-area: legalDisclaimer;
  font-size: ${({ theme }) => theme.custom.helpers.pxToRem(12)};
  font-family: ${({ theme }) => theme.custom.type.sans};
  text-align: center;
  @media ${({ theme }) => theme.custom.layouts.above_large} {
    ul {
      justify-content: flex-end;
    }
  }
`;

// FOOTER ==================

const StyledFooter = styled(GrommetFooter)`
  color: white;
  background: ${({ theme }) => theme.global.colors['dark-3']};
  position: relative;

  a {
    font-family: ${({ theme }) => theme.custom.type.sans};
    font-size: ${({ theme }) => theme.custom.helpers.pxToRem(16)};
    line-height: ${({ theme }) => theme.custom.helpers.pxToRem(28)};
    color: white;
    text-decoration: none;

    @media (hover: hover) {
      &:hover {
          color: white;
          text-decoration: underline;
        }
      }
    }
  }

  > div {
    ${({ theme }) => theme.custom.helpers.LayoutContainer``};
    width: 100%;
    display: grid;
    grid-row-gap: 20px;
    grid-template-areas:
      'siteLogo siteLogo'
      'pwcLink pwcLink'
      'siteLinks legalLinks'
      'socialIcons socialIcons'
      'legalDisclaimer legalDisclaimer';

    padding-top: 40px;
    padding-bottom: 40px;

    @media ${({ theme }) => theme.custom.layouts.above_large} {
      grid-row-gap: 40px;
      grid-column-gap: 60px;
      grid-template-areas:
        'siteLogo siteLinks legalLinks'
        'pwcLink socialIcons socialIcons'
        'legalDisclaimer legalDisclaimer legalDisclaimer';
    }
  }
`;

const LegalLinksData = [
  {
    title: 'Privacy',
    href: 'https://www.pwc.com/us/en/site/privacy.html',
  },
  {
    title: 'Legal',
    href: 'https://www.pwc.com/us/en/site/legal.html',
  },
  {
    title: 'Cookie info',
    href: 'https://www.pwc.com/us/en/site-information.html',
  },
  {
    title: 'Terms of use',
    href: 'https://www.pwc.com/us/en/site/terms-and-conditions.html',
  },
];

const Footer = () => {
  const {
    footerLogo,
    site: {
      siteMetadata: { pages },
    },
  } = useStaticQuery(graphql`
    {
      footerLogo: contentfulAsset(title: { eq: "Footer Logo" }) {
        gatsbyImageData(
          height: 50
          formats: AUTO
          outputPixelDensities: 2
          quality: 100
          layout: FIXED
        )
        description
      }
      site {
        siteMetadata {
          pages {
            title
            href
            items {
              href
              title
            }
          }
        }
      }
    }
  `);
  const year = new Date().getFullYear() 

  return (
    <StyledFooter>
      <div>
        <StyledSiteLogo>
          <Link onDark href="/" title="Home">
            <GatsbyImage
              image={footerLogo.gatsbyImageData}
              alt={footerLogo.description}
            />
          </Link>
        </StyledSiteLogo>
        <PWCLink>
          <Paragraph>
            <Link href="https://www.pwc.com/us/en/products.html">
              PwC Products
            </Link>
          </Paragraph>
        </PWCLink>
        {/* Site Links */}
        <SiteLinks>
          <FooterLinkList>
            {pages.map((page) => (
              <FooterLink as="li" key={page.title}>
                <Link onDark href={page.href} title={page.title}>
                  {page.title}
                </Link>
              </FooterLink>
            ))}
          </FooterLinkList>
        </SiteLinks>
        {/* Legal Links */}
        <LegalLinks>
          <FooterLinkList>
            {LegalLinksData.map((page) => (
              <FooterLink as="li" key={page.title}>
                <Link onDark href={page.href} title={page.title}>
                  {page.title}
                </Link>
              </FooterLink>
            ))}
          </FooterLinkList>
        </LegalLinks>
        {/* Social Icons */}
        <SocialIconsGroup>
          <SocialIcons direction="row" gap="20px">
            {SocialItemsData.filter((i) =>
              ['twitter', 'linkedin'].includes(i.slug),
            ).map((j) => (
              <SocialIcon variant="dark" {...j} key={j.slug} />
            ))}
          </SocialIcons>
        </SocialIconsGroup>
        <LegalDisclaimer>
          <p>Ready Assess will replace legacy products known as Risk Atlas and Risk Proof. Risk Detect will replace legacy products known as Matter Now, Detection Monitoring Hub, and True Comply. Enterprise Control was previously known as Enterprise Insights.
          <br />
            <br />
            The information on this website is intended to outline PwC's general
            product offering and may vary including for particular clients or
            configurations of the product. It is intended for informational
            purposes only and should not be relied upon in making a purchase
            decision, nor incorporated into any contract. It is not a commitment
            to deliver any material, code, or functionality. The development,
            release, and timing of any features or functionality for PwC's
            products remains at PwC's discretion. 
            <br />
            <br />
            © {year} PwC. All rights reserved. PwC refers to the US member firm or one of its subsidiaries or affiliates, and may sometimes
            refer to the PwC network. Each member firm is a separate legal
            entity. Please see www.pwc.com/structure for further details.
          </p>
        </LegalDisclaimer>
      </div>
    </StyledFooter>
  );
};

export default Footer;
